import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  HandHeart, LayoutDashboard, Users, Briefcase, Settings, Bell, 
  LogOut, Menu, X, ChevronDown, UserCircle, Building, BarChart3, MessageSquare, CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_NAME_SHORT = "SHS Union & Somerset County NJ";

const DashboardLayout = () => {
  const { user, logout, isOwner, isSupport } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadSms, setUnreadSms] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/sms/unread-count`);
        setUnreadSms(res.data.unread_count || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const roleDisplayName = (role) => {
    if (role === 'owner') return 'Owner';
    if (role === 'full_admin') return 'Full Admin';
    if (role === 'limited_access') return 'Limited Access';
    if (role === 'va') return 'Team Member';
    if (role === 'support') return 'Support';
    return role;
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Candidates', href: '/dashboard/candidates', icon: Users },
    { name: 'Employees', href: '/dashboard/employees', icon: Building },
    { name: 'Job Listings', href: '/dashboard/jobs', icon: Briefcase },
    { name: 'Notifications', href: '/dashboard/notifications', icon: Bell },
    { name: 'Messages', href: '/dashboard/messages', icon: MessageSquare },
    { name: 'Calendar', href: '/dashboard/calendar', icon: CalendarDays },
    { name: 'Analytics', href: '/dashboard/analytics', icon: BarChart3 },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings, ownerOrSupport: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href || 
      (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
    
    if (item.ownerOnly && !isOwner) return null;
    if (item.ownerOrSupport && !isOwner && !isSupport) return null;
    
    return (
      <Link
        to={item.href}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive 
            ? 'bg-[#E8722A] text-white' 
            : 'text-gray-200 hover:bg-white/10'
        }`}
        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium flex-1">{item.name}</span>
        {item.name === 'Messages' && unreadSms > 0 && (
          <Badge className="bg-[#E8722A] text-white text-xs h-5 min-w-5 flex items-center justify-center rounded-full border-2 border-[#4B2D6E]" data-testid="unread-sms-badge">
            {unreadSms > 99 ? '99+' : unreadSms}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Mobile Header */}
      <header className="lg:hidden bg-[#4B2D6E] text-white py-4 px-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <HandHeart className="h-8 w-8 text-[#E8722A]" />
          <span className="font-bold font-[Lato] text-sm">{BRAND_NAME_SHORT}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-white/10"
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#4B2D6E] transform transition-transform lg:translate-x-0 flex flex-col h-screen lg:h-auto lg:min-h-screen ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          data-testid="sidebar"
        >
          {/* Sidebar Header */}
          <div className="hidden lg:flex items-center gap-3 p-6 border-b border-white/10 flex-shrink-0">
            <HandHeart className="h-10 w-10 text-[#E8722A]" />
            <div>
              <h1 className="font-bold text-white font-[Lato] text-sm">{BRAND_NAME_SHORT}</h1>
              <p className="text-xs text-gray-300">SeniorPeerConnect™ Dashboard</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-2 mt-16 lg:mt-0 flex-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink key={item.name} item={item} />
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-white/10 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-white hover:bg-white/10"
                  data-testid="user-menu-btn"
                >
                  <UserCircle className="h-8 w-8 mr-3" />
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-gray-300">{roleDisplayName(user?.role)}</p>
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-gray-600">
                  <UserCircle className="mr-2 h-4 w-4" />
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer">
                    <Briefcase className="mr-2 h-4 w-4" />
                    View Careers Page
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen lg:min-h-[calc(100vh)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
